import { createContext, useContext, useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router';
import type { MeResponse } from '@sellit/shared';
import { api, ApiError } from '../lib/api';

const MeContext = createContext<MeResponse | null>(null);

export function useMe(): MeResponse {
  const me = useContext(MeContext);
  if (!me) throw new Error('useMe outside RequireAuth');
  return me;
}

/** Loads the signed-in user; sends anyone without a session to the login page. */
export function RequireAuth() {
  const location = useLocation();
  const [state, setState] = useState<{ me?: MeResponse; error?: ApiError }>({});

  useEffect(() => {
    let live = true;
    api.me().then(
      (me) => live && setState({ me }),
      (error: ApiError) => live && setState({ error }),
    );
    return () => {
      live = false;
    };
  }, []);

  if (state.error?.status === 401) {
    return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
  }
  if (state.error) {
    return (
      <main className="page narrow center">
        <p className="error">{state.error.message}</p>
        <button className="btn" onClick={() => window.location.reload()}>
          Try again
        </button>
      </main>
    );
  }
  if (!state.me) return <main className="page narrow center muted">Loading…</main>;
  if (!state.me.agreementAccepted) return <AcceptTerms onAccepted={(me) => setState({ me })} />;
  return (
    <MeContext.Provider value={state.me}>
      <Outlet />
    </MeContext.Provider>
  );
}

/** Sellers and admins accept the current Terms and Privacy Policy once (and again when either changes). */
function AcceptTerms({ onAccepted }: { onAccepted: (me: MeResponse) => void }) {
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!checked) return;
    setBusy(true);
    setError(null);
    try {
      onAccepted(await api.acceptTerms());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save. Try again.');
      setBusy(false);
    }
  }

  return (
    <main className="page narrow">
      <h1>Before you continue</h1>
      <p>
        Please read and accept the <Link to="/terms" target="_blank">Terms of Service</Link> and{' '}
        <Link to="/privacy" target="_blank">Privacy Policy</Link>. In short: you list your items, and you and
        your buyers arrange payment and pickup yourselves. We don't handle money or delivery or check identities, so
        sell only what you own and meet safely.
      </p>
      <form className="stack" onSubmit={submit}>
        <label className="check">
          <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
          <span>I have read and agree to the Terms of Service and Privacy Policy.</span>
        </label>
        {error && <p className="error" role="alert">{error}</p>}
        <button className="btn primary big" disabled={!checked || busy}>{busy ? 'Saving…' : 'Continue'}</button>
      </form>
    </main>
  );
}
